import {
  Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  onCleanup,
  ParentProps,
  Setter,
  untrack,
  useContext,
} from 'solid-js';
import { TcProject, TcRegion } from './types/tc-tcps-api-service.types';
import { useQuery } from '@tanstack/solid-query';
import {
  getAvailableRegionsOptions,
  getProjectDetailsOptions,
  updateProject,
} from '@data-access/connect/tc-tcps/generated';
import { makeMergedPersisted } from '@trimble/solidjs-utils';
import { client as tcTcpsClient } from '@data-access/connect/tc-tcps/generated';
import { getRegionalBaseUrl } from '@data-access/connect/tc-tcps';
import { asError, getLogger } from '@trimble/logging';

export interface ConnectProjectProviderProps {
  connectUrl?: string;
  tcTcpsClient?: typeof tcTcpsClient;
}

export type ConnectProjectProviderComponent = (
  props: ParentProps<ConnectProjectProviderProps>
) => JSX.Element;

export interface ConnectProjectContextState {
  regions: Accessor<TcRegion[] | undefined>;
  selectedRegion: Accessor<TcRegion | undefined>;
  selectedProject: Accessor<TcProject | undefined>;
  selectedProjectId: Accessor<string | undefined>;
  selectedProjectExplorerUrl: Accessor<string | undefined>;
  selectedProjectPropertiesLibraryUrl: Accessor<string | undefined>;
  selectedTenantTrn: Accessor<string | undefined>;
  selectedLocation: Accessor<string | undefined>;
  setSelectedLocation: Setter<string | undefined>;
  setSelectedProjectId: Setter<string | undefined>;
  setSelectedRegion(region: TcRegion | undefined): void;
  projectDetailsLoading: Accessor<boolean>;
  fetchProjectDetailsError: Accessor<Error | undefined>;
  fetchRegionsError: Accessor<Error | undefined>;
}

export const RESULT_LIMIT = 6;

const ConnectProjectContext = createContext<ConnectProjectContextState>();

export const ConnectProjectProvider: ConnectProjectProviderComponent = (
  props
) => {
  const [selectedLocation, setSelectedLocation] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<string | undefined>(undefined),
    {
      name: 'region', // TODO: Why is the persistence for location 'region'? TODO2: Should this be in an enum?
    }
  );

  const [selectedRegion, setSelectedRegionSignal] = createSignal<
    TcRegion | undefined
  >(undefined);

  const setSelectedRegion = (region: TcRegion | undefined): void => {
    setSelectedRegionSignal(region);
    setSelectedLocation(region?.location);
  };

  // Set the selected region when the selected location changes.
  createEffect(() => {
    const location = selectedLocation();
    const regions = tcRegions();
    if (regions === undefined || location === undefined) return undefined;

    setSelectedRegion(regions.find((r) => r.location === location));
  });

  // Update the project last visited on when the project changes.
  createEffect(() => {
    const project = selectedProject();
    const region = untrack(selectedRegion);
    if (project === undefined || region === undefined) return;

    updateProject({
      body: {
        lastVisitedOn: new Date().toISOString(),
      },
      path: {
        projectId: project.id,
      },
      client: props.tcTcpsClient ?? tcTcpsClient,
      baseUrl: getRegionalBaseUrl(region),
    }).catch((err) => {
      getLogger().error(
        'Error updating project last visited on',
        { project, region },
        asError(err)
      );
    });
  });

  const selectedTenantTrn = createMemo(() => {
    const region = selectedRegion();
    if (region === undefined) return undefined;

    const project = selectedProject();
    if (project === undefined) return undefined;

    return `trn:connect:projects:${region.location}:${project.id}`;
  });

  // Query Connect for the regions.
  const options = getAvailableRegionsOptions({
    client: tcTcpsClient,
    baseUrl: tcTcpsClient.getConfig().baseUrl,
  });
  const tcRegionsQuery = useQuery(() => {
    return { ...options };
  });

  const tcRegions = createMemo(() => {
    const query = tcRegionsQuery;

    if (query.isSuccess) return query.data;
    return undefined;
  });

  const fetchRegionsError = createMemo(() => {
    if (tcRegionsQuery.isError) return tcRegionsQuery.error;
    return undefined;
  });

  const [selectedProjectId, setSelectedProjectId] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<string | undefined>(undefined),
    {
      name: 'projectId',
    }
  );

  const abortController = new AbortController();

  const projectDetailsQuery = useQuery(() => {
    const region = selectedRegion();
    const projectId = selectedProjectId();

    return {
      enabled: projectId !== undefined && region !== undefined,
      refetchOnWindowFocus: false,
      ...getProjectDetailsOptions({
        path: {
          projectId: projectId!,
        },
        baseUrl: region ? getRegionalBaseUrl(region) : undefined,
        signal: abortController.signal,
      }),
      placeholderData: (previousData) => previousData,
    };
  });

  onCleanup(() => {
    abortController.abort();
  });

  const fetchProjectDetailsError = createMemo(() => {
    if (abortController.signal.aborted) return undefined;
    if (projectDetailsQuery.isError) return projectDetailsQuery.error;
    return undefined;
  });

  const projectDetailsLoading = createMemo(() => {
    return projectDetailsQuery.isFetching;
  });

  const selectedProject = createMemo(() => {
    const data = projectDetailsQuery.data;
    if (projectDetailsQuery.isSuccess) {
      return data;
    } else {
      return undefined;
    }
  });

  const selectedProjectExplorerUrl = createMemo(() => {
    if (props.connectUrl === undefined) return undefined;

    const projectId = selectedProjectId();
    if (projectId === undefined) return undefined;

    return `${props.connectUrl}/projects/${projectId}/data/folder/`;
  });

  const selectedProjectPropertiesLibraryUrl = createMemo(() => {
    if (props.connectUrl === undefined) return undefined;

    const projectId = selectedProjectId();
    if (projectId === undefined) return undefined;

    return `${props.connectUrl}/projects/${projectId}/property-set-libraries`;
  });

  return (
    <ConnectProjectContext.Provider
      value={{
        regions: tcRegions,
        selectedRegion,
        selectedProject,
        selectedProjectId,
        selectedProjectExplorerUrl,
        selectedProjectPropertiesLibraryUrl,
        setSelectedProjectId,
        selectedTenantTrn,
        selectedLocation,
        setSelectedLocation,
        setSelectedRegion,
        projectDetailsLoading,
        fetchProjectDetailsError,
        fetchRegionsError,
      }}
    >
      {props.children}
    </ConnectProjectContext.Provider>
  );
};

export const useConnectProject = (): ConnectProjectContextState => {
  const context = useContext(ConnectProjectContext);
  if (!context) {
    throw new Error(
      'useConnectProject must be used within a ConnectProjectProvider'
    );
  }
  return context;
};
